import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import LessonMarkdown from "@/components/LessonMarkdown";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { trustLine } from "@/i18n";
import { useT } from "@/hooks/useT";
import { Blend, Gamepad2, Loader2, Play, Square } from "lucide-react";
import MicroChunker from "@/components/MicroChunker";

const LENSES = ["Sports", "History", "Science", "Art", "Biology", "Music", "Warfare", "Cinema"];

export default function ModuleModesModal({ open, onOpenChange, path, stepIndex, mode = "analogy", onCreditsChange }) {
  const { lang } = useT();
  const [lens, setLens] = useState("Science");
  const [custom, setCustom] = useState("");
  const [md, setMd] = useState("");
  const [loading, setLoading] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  const step = path?.steps?.[stepIndex];
  const isAnalogy = mode === "analogy";

  const generate = async () => {
    if (!step) return;
    setLoading(true);
    setMd("");
    try {
      const body = {
        path_id: path.path_id,
        step_index: stepIndex,
        step_title: step.title,
        step_detail: step.detail || step.objective || "",
        ...(isAnalogy ? { lens: (custom.trim() || lens).toLowerCase() } : { scenario: step.detail || "" }),
      };
      const url = isAnalogy ? "/counselor/analogy" : "/counselor/simulate";
      const { data } = await api.post(url, body);
      setMd(data.markdown || "");
    } catch (e) {
      if (e?.response?.status !== 402) {
        toast.error(e?.response?.data?.detail || "Generation failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const listen = async () => {
    if (!md) return toast.error("Generate first");
    if (speaking) {
      window.speechSynthesis?.cancel();
      setSpeaking(false);
      return;
    }
    try {
      // Charge 1 credit first
      const { data } = await api.post("/credits/spend", { reason: "listen", amount: 1 });
      onCreditsChange?.(data.credits);
    } catch (e) {
      const status = e?.response?.status;
      if (status !== 402) toast.error("Listen failed");
      return;
    }
    const synth = window.speechSynthesis;
    if (!synth) return toast.error("Your browser does not support speech synthesis");
    const plain = md.replace(/[#*_>`-]/g, " ").replace(/\s+/g, " ").trim();
    const u = new SpeechSynthesisUtterance(plain.slice(0, 4500));
    u.lang = document.documentElement.lang || "en";
    u.rate = 1;
    u.pitch = 1;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    synth.speak(u);
    setSpeaking(true);
  };

  const Icon = isAnalogy ? Blend : Gamepad2;
  const title = isAnalogy ? "Cross-Pollination" : "Simulation Mode";

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          window.speechSynthesis?.cancel();
          setSpeaking(false);
          setMd("");
        }
        onOpenChange(v);
      }}
    >
      <DialogContent className="glass border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto px-5 sm:px-6 md:px-8" data-testid={`${mode}-modal`}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Icon size={18} className={isAnalogy ? "text-[#ffb067]" : "text-[#8aa8ff]"} /> {title}
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">{step?.title}</DialogDescription>
          <div className="italic text-[11px] text-white/35 font-display tracking-wide pt-0.5" data-testid="trust-line-mode">
            {trustLine(lang)}
          </div>
        </DialogHeader>

        {isAnalogy && (
          <div className="flex flex-wrap gap-2 pt-3" data-testid="lens-picker">
            {LENSES.map((l) => (
              <button
                key={l}
                onClick={() => { setLens(l); setCustom(""); }}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  (custom ? false : lens === l)
                    ? "border-[#ffb067]/60 bg-[#ffb067]/15 text-[#ffd49c]"
                    : "border-white/10 bg-white/[0.03] text-white/65 hover:text-white"
                }`}
                data-testid={`lens-${l.toLowerCase()}`}
              >
                {l}
              </button>
            ))}
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="or custom lens…"
              className="flex-1 min-w-[160px] text-xs px-3 py-1.5 rounded-full border border-white/10 bg-black/40 focus:outline-none focus:border-[#ffb067]/50"
              data-testid="lens-custom"
            />
          </div>
        )}

        <div className="flex items-center gap-2 pt-3">
          <button
            onClick={generate}
            disabled={loading}
            className="btn-primary rounded-lg px-4 py-2 text-sm font-display flex items-center gap-2 disabled:opacity-50"
            data-testid={`btn-${mode}-generate`}
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />}
            {loading ? "Generating…" : md ? "Regenerate" : "Generate"}
          </button>
          {md && (
            <button
              onClick={listen}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-[#00FF9D]/35 text-[#9bffcc] hover:bg-[#00FF9D]/10 transition"
              data-testid="btn-listen"
              title="Listen (1 credit)"
            >
              {speaking ? <Square size={12} /> : <Play size={12} />}
              {speaking ? "Stop" : "Listen · 1 credit"}
            </button>
          )}
        </div>

        <div className="mt-4" data-testid={`${mode}-content`}>
          {loading && !md ? (
            <div className="space-y-2">
              <div className="h-4 rounded bg-white/5 sheen w-2/3" />
              <div className="h-3 rounded bg-white/5 sheen" />
              <div className="h-3 rounded bg-white/5 sheen w-5/6" />
              <div className="h-3 rounded bg-white/5 sheen w-4/6" />
            </div>
          ) : md ? (
            <div className="prose-oak text-[14.5px]">
              <LessonMarkdown
                markdown={md}
                components={{
                  h2: (p) => <h2 className="font-display text-xl text-white mt-3 mb-2" {...p} />,
                  h3: (p) => <h3 className="font-display text-base text-white/95 mt-4 mb-1" {...p} />,
                  p:  (p) => <p className="text-white/80 my-2" {...p} />,
                  ul: (p) => <ul className="list-disc pl-5 text-white/80 space-y-1 my-2 marker:text-[#00FF9D]" {...p} />,
                  li: (p) => <li className="leading-relaxed" {...p} />,
                  strong: (p) => <strong className="text-white font-semibold" {...p} />,
                  blockquote: (p) => <blockquote className="border-l-2 border-[#00FF9D]/60 bg-[#00FF9D]/[0.04] pl-3 py-1.5 text-white/85 my-3" {...p} />,
                }}
              />
              <MicroChunker
                getText={() => md}
                context={`${step?.title || "Module"} · ${isAnalogy ? "Analogy" : "Simulation"}${lens ? " · " + lens : ""}`}
                testIdPrefix={`chunker-${mode}`}
              />
            </div>
          ) : (
            <div className="text-white/40 text-sm">
              {isAnalogy
                ? "Pick a lens (Sports, History, Science, Art, or type your own) and generate."
                : "Generate an interactive scenario for this module. Options + debrief included."}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
